import React, { useState } from 'react';
import { QuestionData } from '@/features/submission_forms/types';
import {
  GripVertical,
  FileText,
  CheckSquare,
  Type,
  Calendar,
  Hash,
  List,
  Upload,
} from 'lucide-react';

interface QuestionNodeProps {
  question: QuestionData;
  path: number[];
  selectedId?: string;
  onSelect: (id: string, path: number[]) => void;
  onMove: (fromIdx: number, toIdx: number) => void;
  readOnly?: boolean;
}

const questionIcons: Record<string, React.ReactNode> = {
  text: <Type className="w-4 h-4" />,
  textarea: <FileText className="w-4 h-4" />,
  select_single: <List className="w-4 h-4" />,
  select_multi: <CheckSquare className="w-4 h-4" />,
  date: <Calendar className="w-4 h-4" />,
  number: <Hash className="w-4 h-4" />,
  file: <Upload className="w-4 h-4" />,
};

export const QuestionNode: React.FC<QuestionNodeProps> = ({
  question,
  path,
  selectedId,
  onSelect,
  onMove,
  readOnly = false,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const isSelected = selectedId === question.id;
  const questionIdx = path[path.length - 1];

  const handleDragStart = (e: React.DragEvent) => {
    setIsDragging(true);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('question-idx', String(questionIdx));
    e.dataTransfer.setData('question-path', JSON.stringify(path));
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const fromIdxStr = e.dataTransfer.getData('question-idx');
    const fromPathStr = e.dataTransfer.getData('question-path');

    if (fromIdxStr && fromPathStr) {
      const fromIdx = parseInt(fromIdxStr, 10);

      onMove(fromIdx, questionIdx);
    }
  };

  return (
    <div
      draggable={!readOnly}
      onDragStart={readOnly ? undefined : handleDragStart}
      onDragEnd={readOnly ? undefined : handleDragEnd}
      onDragOver={readOnly ? undefined : handleDragOver}
      onDrop={readOnly ? undefined : handleDrop}
      onClick={() => onSelect(question.id, path)}
      className={`
        flex items-center gap-2 p-2 rounded-lg cursor-pointer
        hover:bg-gray-50 transition-colors
        ${isDragging ? 'opacity-50' : ''}
        ${isSelected ? 'bg-blue-50 border border-blue-300' : 'border border-transparent'}
      `}
    >
      {/* Drag Handle */}
      {!readOnly && (
        <GripVertical className="w-4 h-4 text-gray-400 cursor-grab active:cursor-grabbing" />
      )}

      {/* Question Type Icon */}
      <div className="text-gray-500">
        {questionIcons[question.kind] || <FileText className="w-4 h-4" />}
      </div>

      {/* Question Prompt */}
      <span className="flex-1 text-sm text-gray-700 truncate">
        {question.prompt}
      </span>

      {/* Badges */}
      <div className="flex gap-1">
        {question.required && (
          <span className="px-1.5 py-0.5 text-xs bg-red-100 text-red-700 rounded">
            Required
          </span>
        )}
        {question.details && (
          <span className="px-1.5 py-0.5 text-xs bg-yellow-100 text-yellow-700 rounded">
            Details
          </span>
        )}
        {question.depends_on && (
          <span className="px-1.5 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">
            Conditional
          </span>
        )}
      </div>
    </div>
  );
};
